import { and, coalesce, eq, type InitialQueryBuilder, isNull, not, type QueryResult } from "@tanstack/db";

import type { WorkspaceMembership as WsWorkspaceMembership } from "@storyboard/web-client";
import { idx, rowsByIndex, store } from "@/store/store";
import type { Permission, WorkspaceMembership } from "@/store/types";

export async function bulkUpsertWorkspaceMembership(records: WsWorkspaceMembership[]) {
  if (records.length === 0) return;
  const data = records.map(
    (r): WorkspaceMembership => ({
      id: r.id,
      workspaceId: r.workspaceId,
      accountId: r.accountId ?? null,
      status: r.status,
      role: r.role,
    }),
  );
  store.upsert("workspaceMembership", data, (existing, next) => ({
    ...existing,
    accountId: next.accountId,
    status: next.status,
    role: next.role,
  }));
}

export async function listFeedPermissions(workspaceMembershipId: string, feedId: string): Promise<Permission[]> {
  return rowsByIndex("permission", idx.permissionsByFeed, feedId).filter(
    (permission) => permission.workspaceMembershipId === workspaceMembershipId,
  );
}

export type WorkspaceMembershipRow = {
  accountId: string | null;
  role: string;
  membershipId: string;
  email: string | null;
  phoneNumber: string | null;
  name: string | null;
  avatarColor: string | null;
};

/** One row per membership per invitation (the SQL left-joined invitations without deduping). */
export const workspaceMembershipsQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ membership: store.collection("workspaceMembership") })
    .leftJoin({ account: store.collection("account") }, ({ membership, account }) =>
      eq(membership.accountId, account.id),
    )
    .leftJoin({ invitation: store.collection("directWsInvitation") }, ({ membership, invitation }) =>
      eq(membership.id, invitation.workspaceMembershipId),
    )
    .where(({ membership }) => and(eq(membership.workspaceId, workspaceId), eq(membership.status, "active")))
    .select(({ membership, account, invitation }) => ({
      accountId: membership.accountId,
      role: membership.role,
      membershipId: membership.id,
      email: coalesce(account.email, invitation.email),
      phoneNumber: coalesce(account.phoneNumber, invitation.phoneNumber),
      name: coalesce(account.name, invitation.name),
      avatarColor: account.avatarColor,
    }));

/** Active memberships with a claimed account — the member count is the row count. */
export const workspaceMembersQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ membership: store.collection("workspaceMembership") })
    .where(({ membership }) =>
      and(eq(membership.workspaceId, workspaceId), eq(membership.status, "active"), not(isNull(membership.accountId))),
    );

export const myWorkspaceMembershipsQuery =
  (accountId: string, currentWorkspaceId: string) => (q: InitialQueryBuilder) =>
    q
      .from({ membership: store.collection("workspaceMembership") })
      .where(({ membership }) =>
        and(
          eq(membership.accountId, accountId),
          eq(membership.workspaceId, currentWorkspaceId),
          eq(membership.status, "active"),
        ),
      );

export const justWorkspaceMembershipsQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ membership: store.collection("workspaceMembership") })
    .where(({ membership }) => and(eq(membership.workspaceId, workspaceId), eq(membership.status, "active")));

export const workspaceMembersTableQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ membership: store.collection("workspaceMembership") })
    .innerJoin({ account: store.collection("account") }, ({ membership, account }) =>
      eq(membership.accountId, account.id),
    )
    .where(({ membership }) => and(eq(membership.workspaceId, workspaceId), eq(membership.status, "active")))
    .select(({ membership, account }) => ({
      id: account.id,
      email: account.email,
      phoneNumber: account.phoneNumber,
      name: account.name,
      mine: account.mine,
      role: membership.role,
      membershipId: membership.id,
    }))
    .orderBy(({ account }) => account.name, { direction: "asc", nulls: "last" });

/** One row of {@link workspaceMembersTableQuery}, derived from its projection so the two cannot drift apart. */
export type WorkspaceMemberTableRow = QueryResult<ReturnType<ReturnType<typeof workspaceMembersTableQuery>>>;
